import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Award, TrendingUp, Sparkles, ArrowRight, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import StorytellingStyleProfileAssessment from '../../data/assessments/StorytellingStyleProfile';
import Card from '../shared/Card';
import Button from '../shared/Button';

const StorytellingStyleAssessment = () => {
  const navigate = useNavigate();
  const { assessmentResults, setAssessmentResults } = useApp();

  const [currentStep, setCurrentStep] = useState('intro'); // 'intro', 'questions', 'results'
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  const assessment = StorytellingStyleProfileAssessment;
  const currentQuestion = assessment.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / assessment.questionCount) * 100;

  // Calculate scores from responses
  // Scores the answers passed in. The last answer is added to state in the
  // same handler that scores, so reading `responses` here dropped it.
  const calculateScores = (answers) => {
    const dimensionScores = {};
    const dimensionCounts = {};

    // Initialize all dimensions
    Object.keys(assessment.dimensions).forEach(dim => {
      dimensionScores[dim] = 0;
      dimensionCounts[dim] = 0;
    });

    // Aggregate weighted scores
    answers.forEach(response => {
      const question = assessment.questions.find(q => q.id === response.questionId);
      const option = question.options[response.selectedIndex];

      if (option.weights) {
        Object.entries(option.weights).forEach(([dimension, weight]) => {
          if (dimensionScores[dimension] !== undefined) {
            dimensionScores[dimension] += weight;
            dimensionCounts[dimension]++;
          }
        });
      }
    });

    // Normalize to 0-100 scale
    // Weights range from -2 to +2, so we need to convert
    const normalizedScores = {};
    Object.keys(dimensionScores).forEach(dim => {
      if (dimensionCounts[dim] > 0) {
        const avgScore = dimensionScores[dim] / dimensionCounts[dim];
        // Convert from -2 to +2 range to 0-100
        normalizedScores[dim] = Math.round(((avgScore + 2) / 4) * 100);
      } else {
        normalizedScores[dim] = 50; // Default to middle if no data
      }
    });

    return normalizedScores;
  };

  // Determine profile type from scores
  const determineProfileType = (scores) => {
    // Calculate average score
    const avgScore = Object.values(scores).reduce((a, b) => a + b, 0) / 7;

    // Get individual dimension scores
    const structure = scores.narrativeStructure ?? 50;
    const emotional = scores.emotionalCraft ?? 50;
    const context = scores.contextAdaptation ?? 50;
    const vulnerability = scores.vulnerabilityCalibration ?? 50;
    const delivery = scores.deliveryPresence ?? 50;
    const listening = scores.storyListening ?? 50;

    // Profile type logic (matches assessment definition)
    if (avgScore >= 75) {
      return 'skilledStoryteller';
    }

    if (avgScore >= 55) {
      return 'reflectiveBuilder';
    }

    if (emotional >= 65 && delivery >= 65 && (vulnerability < 50 || listening < 50)) {
      return 'expressivePerformer';
    }

    if (structure >= 60 && context >= 60 && emotional < 50) {
      return 'analyticalNarrator';
    }

    if (listening >= 65 && vulnerability >= 60 && (structure < 50 || context < 50)) {
      return 'naturalConnector';
    }

    return 'emergent';
  };

  const handleStartAssessment = () => {
    setCurrentStep('questions');
    setCurrentQuestionIndex(0);
    setResponses([]);
    setSelectedOption(null);
  };

  const handleOptionSelect = (optionIndex) => {
    setSelectedOption(optionIndex);
  };

  const handleNextQuestion = () => {
    if (selectedOption === null) return;

    // Save response
    const newResponses = [
      ...responses,
      {
        questionId: currentQuestion.id,
        selectedIndex: selectedOption,
        questionText: currentQuestion.text,
        answerText: currentQuestion.options[selectedOption].text
      }
    ];
    setResponses(newResponses);
    setSelectedOption(null);

    // Check if this was the last question
    if (currentQuestionIndex === assessment.questionCount - 1) {
      // Calculate final scores and generate results
      const scores = calculateScores(newResponses);
      const profileTypeKey = determineProfileType(scores);
      const profileType = assessment.profileTypes[profileTypeKey];

      // Build dimensions object with scores
      const dimensionsWithScores = {};
      Object.entries(assessment.dimensions).forEach(([key, dimension]) => {
        dimensionsWithScores[key] = {
          ...dimension,
          score: scores[key] ?? 50
        };
      });

      // Save to context
      const results = {
        profileType,
        dimensions: dimensionsWithScores,
        avgScore: Math.round(Object.values(scores).reduce((a, b) => a + b, 0) / 7),
        responses: newResponses,
        dateTaken: new Date().toISOString(),
        assessmentId: assessment.id,
        // Store as baseline if no previous results exist
        baseline: assessmentResults ? assessmentResults.baseline : {
          dimensions: dimensionsWithScores,
          avgScore: Math.round(Object.values(scores).reduce((a, b) => a + b, 0) / 7),
          dateTaken: new Date().toISOString()
        }
      };

      setAssessmentResults(results);
      setCurrentStep('results');
    } else {
      // Move to next question
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      // Restore previous selection
      const previousResponse = responses[currentQuestionIndex - 1];
      if (previousResponse) {
        setSelectedOption(previousResponse.selectedIndex);
        // Remove the response for this question
        setResponses(responses.slice(0, -1));
      }
    }
  };

  const handleRetake = () => {
    setCurrentStep('intro');
    setCurrentQuestionIndex(0);
    setResponses([]);
    setSelectedOption(null);
  };

  // Intro Screen
  if (currentStep === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 md:px-8 pb-8 pt-40">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6 inline-block"
            >
              <img
                src="/images/assessment.png"
                alt="Storytelling Assessment"
                className="w-32 h-32 md:w-44 md:h-44 object-contain rounded-2xl mx-auto"
              />
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {assessment.title}
            </h1>
            <p className="text-xl text-slate-300 mb-2">
              {assessment.subtitle}
            </p>
            <div className="flex items-center justify-center gap-4 text-slate-400 text-sm">
              <span>{assessment.duration}</span>
              <span>•</span>
              <span>{assessment.questionCount} questions</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-white mb-4">
                {assessment.introduction.heading}
              </h2>
              <div className="prose prose-invert max-w-none mb-6">
                {assessment.introduction.content.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-slate-300 mb-4 last:mb-0">
                    {paragraph.replace(/\*\*(.*?)\*\*/g, '$1')}
                  </p>
                ))}
              </div>

              <div className="flex gap-4 justify-center mt-8">
                <Button
                  variant="secondary"
                  onClick={() => navigate('/dashboard')}
                >
                  <Home className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
                <Button onClick={handleStartAssessment} className="px-8">
                  Begin Assessment
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Questions Screen
  if (currentStep === 'questions') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 md:px-8 pb-8 pt-40">
        <div className="max-w-3xl mx-auto">
          {/* Progress Bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">
                Question {currentQuestionIndex + 1} of {assessment.questionCount}
              </span>
              <span className="text-sm text-purple-400 font-semibold">
                {Math.round(progress)}% Complete
              </span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <motion.div
                className="h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </motion.div>

          {/* Question Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-8 mb-6">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {currentQuestion.text}
                </h2>

                <div className="space-y-3">
                  {currentQuestion.options.map((option, idx) => (
                    <motion.button
                      key={idx}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => handleOptionSelect(idx)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        selectedOption === idx
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                          selectedOption === idx
                            ? 'border-purple-500 bg-purple-500'
                            : 'border-slate-600'
                        }`}>
                          {selectedOption === idx && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <span className="text-slate-200">{option.text}</span>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </Card>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="secondary"
                  onClick={handlePreviousQuestion}
                  disabled={currentQuestionIndex === 0}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                <Button
                  onClick={handleNextQuestion}
                  disabled={selectedOption === null}
                  className="px-8"
                >
                  {currentQuestionIndex === assessment.questionCount - 1 ? 'See Results' : 'Next'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // Results Screen
  if (currentStep === 'results' && assessmentResults) {
    const { profileType, dimensions } = assessmentResults;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 md:px-8 pb-8 pt-40">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Award className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Your Storytelling Profile
            </h1>
            <p className="text-xl text-slate-300">
              What your answers suggest about how you tell stories
            </p>
          </motion.div>

          {/* Profile Type */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Card className="p-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-white mb-2">
                  {profileType.name}
                </h2>
                <p className="text-xl text-purple-200 mb-4">
                  {profileType.tagline}
                </p>
                <p className="text-slate-300 max-w-2xl mx-auto">
                  {profileType.description}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mt-8">
                {/* Strengths */}
                <div>
                  <h3 className="text-sm font-semibold text-purple-300 uppercase mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Your Strengths
                  </h3>
                  <ul className="space-y-2">
                    {profileType.strengths.map((strength, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-300">
                        <span className="text-green-400 mt-1">✓</span>
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Growth Areas */}
                <div>
                  <h3 className="text-sm font-semibold text-amber-300 uppercase mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Growth Opportunities
                  </h3>
                  <ul className="space-y-2">
                    {profileType.growthAreas.map((area, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-300">
                        <span className="text-amber-400 mt-1">→</span>
                        <span>{area}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Dimension Scores */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <Card className="p-8">
              <h3 className="text-2xl font-bold text-white mb-6">
                Your 7 Storytelling Dimensions
              </h3>
              <div className="space-y-4">
                {Object.entries(dimensions).map(([key, dimension]) => (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{dimension.icon}</span>
                        <span className="text-white font-medium">{dimension.name}</span>
                      </div>
                      <span className="text-purple-400 font-bold">{dimension.score}/100</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-3">
                      <motion.div
                        className="h-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${dimension.score}%` }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                      />
                    </div>
                    <p className="text-sm text-slate-400">{dimension.description}</p>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>

          {/* Next Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <Card className="p-8 bg-gradient-to-br from-teal-500/5 to-blue-500/5 border-teal-500/20">
              <h3 className="text-2xl font-bold text-white mb-4">
                Your Recommended Path
              </h3>
              <ul className="space-y-3 mb-6">
                {profileType.nextSteps.map((step, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-0.5">
                      {idx + 1}
                    </div>
                    <span className="text-slate-300">{step}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-3 sm:gap-4 justify-center pt-6 border-t border-teal-500/20">
                <Button
                  variant="secondary"
                  onClick={handleRetake}
                >
                  Retake Assessment
                </Button>
                <Button
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate('/modules')}
                >
                  Explore Modules
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return null;
};

export default StorytellingStyleAssessment;
